import { ContentCopy, Check, FilterList } from '@mui/icons-material';
import { Box, Chip, IconButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import { ReactNode } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { FunctionField, useListContext } from 'react-admin';
import { useCreateNavigationTargetPath } from './useCreateNavigationTargetPath';

type CopiableFieldProps = {
  content: string;
  small?: boolean;
  targetNavigationResource?: string;
  targetNavigationFilters?: Record<string, any>;
  filter?: Record<string, any>;
  copyLink?: boolean;
};

export const CopiableField = ({
  content,
  small,
  targetNavigationResource,
  targetNavigationFilters,
  filter,
  copyLink = false,
}: CopiableFieldProps) => {
  const [contentCopied, setContentCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!contentCopied) {
      return undefined;
    }

    const timeout = setTimeout(() => setContentCopied(false), 15000);

    return () => clearTimeout(timeout);
  }, [contentCopied]);

  useEffect(() => {
    if (!linkCopied) {
      return undefined;
    }

    const timeout = setTimeout(() => setLinkCopied(false), 2000);

    return () => clearTimeout(timeout);
  }, [linkCopied]);

  const targetPath = useCreateNavigationTargetPath(
    targetNavigationResource,
    targetNavigationFilters,
  );

  // useLogEffect("CopiableField targetPath", targetPath);

  const onCopyContent: React.EventHandler<any> = useCallback(
    async e => {
      e.preventDefault();

      await navigator.clipboard.writeText(content);
      setContentCopied(true);
    },
    [content],
  );

  const onCopyLink: React.EventHandler<any> = useCallback(
    async e => {
      if (!copyLink) {
        return;
      }

      e.preventDefault();

      const link = `${location.protocol}//${location.host}${targetPath}`;

      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
    },
    [targetPath, copyLink],
  );

  const { setFilters } = useListContext();

  const onFilter = useCallback(() => {
    const displayedFilters = Object.fromEntries(
      Object.keys(filter || {}).map(key => [key, true]),
    );

    setFilters(filter, displayedFilters);
  }, [filter, setFilters]);

  if (!content) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center">
      <Chip
        label={content}
        color={linkCopied ? 'success' : undefined}
        clickable
        {...(small ? { size: 'small' } : {})}
        {...(copyLink ? { onClick: onCopyLink } : {})}
        {...(targetPath
          ? {
              component: 'a',
              href: targetPath,
              icon: !copyLink ? (
                <OpenInNewIcon />
              ) : linkCopied ? (
                <Check />
              ) : (
                <LinkIcon />
              ),
            }
          : {})}
        onDelete={onCopyContent}
        deleteIcon={contentCopied ? <Check /> : <ContentCopy />}
      />
      {filter && (
        <IconButton onClick={onFilter}>
          <FilterList />
        </IconButton>
      )}
    </Box>
  );

  // return (
  //   <Box display="flex" alignItems="center">
  //     <Typography
  //       {...(small ? { fontSize: ".85rem" } : {})}
  //       marginRight="0.5rem"
  //     >
  //       {content}
  //     </Typography>
  //     <IconButton
  //       style={{ backgroundColor: isCopied ? "#8A8" : undefined }}
  //       onClick={onCopy}
  //       {...(small ? { size: "small" } : {})}
  //     >
  //       {isCopied ? <Check /> : <ContentCopy />}
  //     </IconButton>
  //   </Box>
  // );
};

export type CopiableIdDataGridFieldProps = {
  source?: string;
  referenceField?: string;
  label: string;
  copyLink?: boolean;
  formatFilter?: (value: any) => any;
  render?: (doc: any) => string | ReactNode;
  filter?: [string, string?];
  getFilterValue?: (doc: any) => any;
  additionalFilter?: Record<string, any>;
} & Pick<
  CopiableFieldProps,
  'targetNavigationFilters' | 'targetNavigationResource'
>;

export const CopiableIdDataGridField = ({
  label,
  source = 'id',
  referenceField = '_id',
  targetNavigationResource,
  targetNavigationFilters,
  copyLink = false,
  formatFilter = (value: any) => value,
  render = (doc: any) => doc[source],
  filter: [filter, filterField = source] = ['', undefined],
  additionalFilter = {},
}: CopiableIdDataGridFieldProps) => (
  <FunctionField
    label={label}
    source={source}
    render={(doc: any) => (
      <CopiableField
        copyLink={copyLink}
        {...(targetNavigationResource && referenceField
          ? {
              targetNavigationResource,
              targetNavigationFilters: {
                [referenceField]: formatFilter(doc[filterField]),
                ...targetNavigationFilters,
              },
            }
          : {
              targetNavigationFilters,
            })}
        content={render ? render(doc) : doc[source]}
        filter={{
          ...(filter ? { [filter]: formatFilter(doc[source]) } : {}),
          ...additionalFilter,
        }}
      />
    )}
  />
);
